import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts"
import {
  formatCurrency, formatNumber, formatPercent,
  familiaConfig, descargarCSV,
} from "./helpers"

const Stat = ({ label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 160px", minWidth: 0,
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "12px 14px",
  }}>
    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: accent, marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
)
Stat.propTypes = { label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const DrawerDetalleSucursal = ({ abierto, sucursal, onCerrar }) => {
  // Bloquea scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (abierto) {
      document.body.style.overflow = "hidden"
      return () => { document.body.style.overflow = "" }
    }
  }, [abierto])

  const totales = sucursal?.totales || {}
  const impuestos = useMemo(() => sucursal?.impuestos || [], [sucursal])
  // Bloque excluidos = nuevo (con fallback a `exentos` por compat con back legacy)
  const excluidos = sucursal?.excluidos ?? sucursal?.exentos ?? {}
  // Si el back ya manda total_excluidos / porcentaje_excluido en totales, usamos eso;
  // si no, derivamos del bloque excluidos.
  const totalExcluido = totales.total_excluidos
    ?? totales.total_exentos
    ?? excluidos.importe
    ?? excluidos.base_gravable
    ?? 0
  const pctExcluido = totales.porcentaje_excluido ?? totales.porcentaje_exento ?? 0
  const alertas = sucursal?.alertas || []

  // Donut data por tipo de impuesto
  const donutData = useMemo(() =>
    impuestos.map(imp => ({
      name: `${imp.tipo_impuesto} · ${imp.tasa}%`,
      value: Number(imp.valor_impuesto) || 0,
      familia: imp.familia,
      nombre_completo: imp.nombre_impuesto,
      color: familiaConfig(imp.familia).color,
    })),
    [impuestos],
  )

  // Bar data: base vs impuesto por tipo
  const barData = useMemo(() =>
    impuestos.map(imp => ({
      tipo: `${imp.tipo_impuesto}·${imp.tasa}%`,
      familia: imp.familia,
      base: Number(imp.base_gravable) || 0,
      impuesto: Number(imp.valor_impuesto) || 0,
    })),
    [impuestos],
  )

  const handleExportCSV = () => {
    const headers = ["Tipo", "Nombre", "Familia", "Tasa", "Líneas", "Base gravable", "Valor impuesto", "Total con impuesto", "Participación"]
    const rows = impuestos.map(i => [
      i.tipo_impuesto, i.nombre_impuesto, i.familia, i.tasa,
      i.cantidad_lineas, i.base_gravable, i.valor_impuesto, i.total_con_impuesto,
      formatPercent(i.participacion_pct, 2),
    ])
    descargarCSV(`impuestos_${sucursal.sucursal.replace(/\s+/g, "_")}.csv`, headers, rows)
  }

  if (!abierto || !sucursal) return null

  return (
    <>
      {/* Backdrop */}
      <div onClick={onCerrar} style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        zIndex: 1040, backdropFilter: "blur(2px)",
      }} />
      {/* Drawer */}
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(900px, 95vw)", background: "#f8fafc", zIndex: 1041,
        display: "flex", flexDirection: "column",
        boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
        animation: "slideInRight .25s ease",
      }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(40px); opacity: .6 } to { transform: translateX(0); opacity: 1 } }`}</style>

        {/* Header */}
        <div style={{
          background: "#fff", borderBottom: "1px solid #e2e8f0",
          padding: "14px 20px", display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: "#dbeafe", color: "#1e40af",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, flexShrink: 0,
          }}>
            <i className="mdi mdi-source-branch" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Detalle de sucursal
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {sucursal.sucursal}
            </div>
            <div style={{ fontSize: 11, color: "#64748b" }}>
              {impuestos.length} {impuestos.length === 1 ? "tipo" : "tipos"} de impuesto · {formatNumber(totales.total_lineas)} líneas
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-success me-2" onClick={handleExportCSV}>
            <i className="mdi mdi-file-delimited me-1" />
            CSV
          </button>
          <button type="button" onClick={onCerrar} style={{
            background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
            color: "#475569", cursor: "pointer", padding: "6px 10px", fontSize: 13,
          }}>
            <i className="mdi mdi-close me-1" />Cerrar
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* KPIs */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <Stat label="Base gravable" value={formatCurrency(totales.total_base_gravable)} accent="#3b82f6" />
            <Stat label="Impuestos" value={formatCurrency(totales.total_impuestos)} accent="#10b981"
              sub={`${formatNumber(totales.total_lineas)} líneas`} />
            <Stat label="Total con imp." value={formatCurrency(totales.total_con_impuestos)} accent="#8b5cf6" />
            <Stat label="Excluidos" value={formatCurrency(totalExcluido)} accent="#f59e0b"
              sub={`${formatPercent(pctExcluido, 2)}`} />
            <Stat label="% Gravado" value={formatPercent(totales.porcentaje_gravado, 2)} accent="#3b82f6"
              sub={`${formatNumber(totales.total_lineas)} líneas`} />
          </div>

          {/* Alertas */}
          {alertas.length > 0 && (
            <div style={{
              background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10,
              padding: "10px 14px",
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
                <i className="mdi mdi-alert-circle-outline me-1" />
                Alertas detectadas
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {alertas.map((a, i) => (
                  <span key={i} style={{
                    padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                    background: "#fff", color: "#92400e", border: "1px solid #fde68a",
                  }}>
                    {a.desc}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Donut + Bar lado a lado */}
          {impuestos.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
                <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 8 }}>
                  <i className="mdi mdi-chart-donut me-2 text-primary" />
                  Distribución por tipo
                </h6>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={donutData} dataKey="value" nameKey="name"
                      innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip
                      formatter={(v, _, p) => [formatCurrency(v), p.payload.nombre_completo]}
                    />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
                <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 8 }}>
                  <i className="mdi mdi-chart-bar me-2 text-primary" />
                  Base vs Impuesto por tipo
                </h6>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="tipo" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
                    <Tooltip formatter={v => formatCurrency(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="base"     name="Base gravable" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="impuesto" name="Impuesto"      fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tabla detalle por tipo */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0" }}>
              <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
                <i className="mdi mdi-format-list-bulleted-type me-2 text-primary" />
                Detalle por tipo de impuesto
              </h6>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={th}>Tipo</th>
                    <th style={th}>Nombre</th>
                    <th style={{ ...th, textAlign: "center" }}>Familia</th>
                    <th style={{ ...th, textAlign: "center" }}>Tasa</th>
                    <th style={{ ...th, textAlign: "right" }}>Líneas</th>
                    <th style={{ ...th, textAlign: "right" }}>Base</th>
                    <th style={{ ...th, textAlign: "right" }}>Impuesto</th>
                    <th style={{ ...th, textAlign: "right" }}>Total con imp.</th>
                    <th style={{ ...th, textAlign: "right" }}>Participación</th>
                  </tr>
                </thead>
                <tbody>
                  {impuestos.map((i, idx) => {
                    const cfg = familiaConfig(i.familia)
                    return (
                      <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                        <td style={{ ...td, fontFamily: "monospace", fontWeight: 600, color: "#1e40af" }}>{i.tipo_impuesto}</td>
                        <td style={{ ...td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={i.nombre_impuesto}>
                          {i.nombre_impuesto}
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <span style={{
                            padding: "1px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
                            background: cfg.bg, color: cfg.color,
                          }}>{i.familia}</span>
                        </td>
                        <td style={{ ...td, textAlign: "center", fontWeight: 600 }}>{formatPercent(i.tasa, 0)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{formatNumber(i.cantidad_lineas)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{formatCurrency(i.base_gravable)}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 600, color: "#10b981" }}>{formatCurrency(i.valor_impuesto)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{formatCurrency(i.total_con_impuesto)}</td>
                        <td style={{ ...td, textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                            <div style={{ width: 50, height: 5, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
                              <div style={{
                                width: `${Math.min(100, i.participacion_pct)}%`,
                                height: "100%", background: cfg.color,
                              }} />
                            </div>
                            <strong>{formatPercent(i.participacion_pct, 1)}</strong>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Excluidos */}
          {excluidos && (excluidos.cantidad_lineas > 0 || excluidos.importe > 0 || excluidos.base_gravable > 0) && (
            <div style={{
              background: "linear-gradient(135deg, #fff7ed, #ffffff)",
              border: "1px solid #fed7aa", borderLeft: "4px solid #f59e0b",
              borderRadius: 12, padding: "14px 18px",
            }}>
              <h6 style={{ margin: 0, color: "#92400e", fontWeight: 700, marginBottom: 8 }}>
                <i className="mdi mdi-shield-off-outline me-2" />
                Productos excluidos
              </h6>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#92400e", textTransform: "uppercase", fontWeight: 700 }}>Líneas</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{formatNumber(excluidos.cantidad_lineas)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#92400e", textTransform: "uppercase", fontWeight: 700 }}>Importe</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
                    {formatCurrency(excluidos.importe ?? excluidos.base_gravable)}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 200, display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "#475569", fontStyle: "italic" }}>
                    <i className="mdi mdi-information-outline me-1" />
                    {excluidos.descripcion || "Productos sin tipo de impuesto (excluidos del régimen IVA)"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}

const th = {
  background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
  padding: "8px 10px", textAlign: "left",
  fontSize: 10, color: "#475569", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.4,
}
const td = {
  padding: "8px 10px", borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#0f172a", verticalAlign: "middle",
}

DrawerDetalleSucursal.propTypes = {
  abierto: PropTypes.bool,
  sucursal: PropTypes.object,
  onCerrar: PropTypes.func.isRequired,
}

export default DrawerDetalleSucursal
